
import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';

export const ScrollReveal: React.FC<{
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
}> = ({ children, className = "", delay = 0, direction = 'up' }) => {
  const offsets = {
    up: { y: 28, x: 0 },
    down: { y: -28, x: 0 },
    left: { x: 28, y: 0 },
    right: { x: -28, y: 0 },
    none: { x: 0, y: 0 }
  };

  return (
    <motion.div
      initial={{ opacity: 0, ...offsets[direction] }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

interface ButtonProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost' | 'cta';
  className?: string;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  ariaLabel?: string;
}

export const Button: React.FC<ButtonProps> = ({ 
  children, 
  onClick, 
  variant = 'primary', 
  className = "", 
  disabled = false, 
  type = "button",
  ariaLabel
}) => {
  const base = "px-6 py-3 rounded-2xl font-medium text-[16px] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-95 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2";
  const variants = {
    primary: "bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm hover:shadow-md hover:shadow-indigo-600/20 hover:-translate-y-[1px]",
    cta: "bg-action text-white hover:opacity-90 shadow-sm hover:shadow-md hover:shadow-action/20 hover:-translate-y-[1px]",
    secondary: "bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
    outline: "border border-indigo-200 text-indigo-600 hover:bg-indigo-50",
    danger: "bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white transition-colors duration-300",
    ghost: "bg-transparent border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-indigo-600 hover:border-slate-300"
  };
  return <button type={type} disabled={disabled} onClick={onClick} aria-label={ariaLabel} className={`${base} ${variants[variant]} ${className}`}>{children}</button>;
};

export const BrandLogo: React.FC<{ className?: string, iconOnly?: boolean, textColor?: string, hideTagline?: boolean }> = ({ className = "", iconOnly = false, textColor = "text-slate-900", hideTagline = false }) => (
  <div className={`flex items-center gap-4 ${className}`} aria-label="ROVITA Medical Telemedicine">
    <div className="w-10 h-10 bg-transparent rounded-[14px] flex items-center justify-center overflow-hidden shadow-sm transition-transform duration-300">
      <img src="/logo.png" alt="ROVITA Medical Symbol" className="w-full h-full object-contain" />
    </div>
    {!iconOnly && (
      <div className="flex flex-col -gap-1">
        <span className={`text-[22px] font-bold tracking-tight ${textColor}`}>ROVITA</span>
        {!hideTagline && (
          <span className={`text-[9px] font-semibold uppercase tracking-[0.1em] ${textColor === 'text-white' ? 'text-white/70' : 'text-slate-400'}`}>Compassionate Healthcare</span>
        )}
      </div>
    )}
  </div>
);

export const SectionTitle: React.FC<{ title: string; subtitle?: string; light?: boolean; centered?: boolean }> = ({ title, subtitle, light = false, centered = false }) => (
  <div className={`mb-12 ${centered ? 'text-center' : ''}`}>
    <h2 className={`text-3xl md:text-5xl font-bold mb-4 tracking-tight ${light ? 'text-white' : 'text-slate-900'}`}>{title}</h2>
    {subtitle && <p className={`text-lg md:text-xl font-medium max-w-2xl ${centered ? 'mx-auto' : ''} ${light ? 'text-indigo-100' : 'text-slate-700'}`}>{subtitle}</p>}
  </div>
);

export const CookieBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const consent = document.cookie.split('; ').find(row => row.startsWith('cookieConsent='));
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const acceptCookies = () => {
    document.cookie = "cookieConsent=true; max-age=31536000; path=/";
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div role="region" aria-label="Cookie Privacy Preferences" className="fixed bottom-0 left-0 right-0 z-[999] bg-slate-900 text-white p-6 md:p-8 shadow-2xl border-t-4 border-indigo-600 animate-slide-up">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex-1">
          <h3 className="text-lg font-black mb-2 flex items-center gap-2">
            <i className="fa-solid fa-cookie-bite text-indigo-400" aria-hidden="true"></i> Cookie Preferences
          </h3>
          <p className="text-slate-400 text-lg font-medium leading-relaxed">
            We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies in accordance with GDPR and our Privacy Policy.
          </p>
        </div>
        <div className="flex gap-4 shrink-0 w-full md:w-auto">
          <button onClick={() => setIsVisible(false)} aria-label="Decline Optional Cookies" className="flex-1 md:flex-none px-6 py-3 rounded-xl font-bold text-sm text-slate-400 hover:text-white transition-colors">Decline</button>
          <button onClick={acceptCookies} aria-label="Accept All Cookies" className="flex-1 md:flex-none px-6 py-3 rounded-xl font-bold text-sm bg-indigo-600 text-white hover:bg-indigo-500 transition-colors shadow-lg shadow-indigo-500/20">Accept All</button>
        </div>
      </div>
    </div>
  );
};

export const ScrollNavigation: React.FC<{ nextId?: string; prevId?: string; isLast?: boolean }> = ({ nextId, prevId, isLast }) => {
  const scrollTo = (id: string) => {
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    });
  };

  const scrollToTop = () => {
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  return (
    <nav aria-label="Page Section Navigation" className="flex justify-center items-center gap-8 py-16">
      {prevId && (
        <button 
          onClick={() => scrollTo(prevId)}
          className="w-16 h-16 rounded-full bg-white border-2 border-slate-200 text-[#008080] flex items-center justify-center hover:bg-[#008080] hover:text-white hover:border-[#008080] transition-all shadow-md active:scale-90 group focus:outline-none focus:ring-2 focus:ring-[#008080]"
          title="Scroll to previous section"
          aria-label="Scroll to previous section"
        >
          <i className="fa-solid fa-arrow-up text-xl group-hover:scale-110 transition-transform" aria-hidden="true"></i>
        </button>
      )}
      {nextId && !isLast && (
        <button 
          onClick={() => scrollTo(nextId)}
          className="w-16 h-16 rounded-full bg-white border-2 border-slate-200 text-[#008080] flex items-center justify-center hover:bg-[#008080] hover:text-white hover:border-[#008080] transition-all shadow-md active:scale-90 group focus:outline-none focus:ring-2 focus:ring-[#008080]"
          title="Scroll to next section"
          aria-label="Scroll to next section"
        >
          <i className="fa-solid fa-arrow-down text-xl group-hover:scale-110 transition-transform" aria-hidden="true"></i>
        </button>
      )}
      {isLast && (
        <button 
          onClick={scrollToTop}
          className="w-16 h-16 rounded-full bg-[#008080] text-white flex items-center justify-center hover:bg-[#006666] transition-all shadow-xl shadow-[#008080]/20 active:scale-90 group focus:outline-none focus:ring-2 focus:ring-[#008080]"
          title="Return to top of page"
          aria-label="Return to top of page"
        >
          <i className="fa-solid fa-angles-up text-xl group-hover:-translate-y-1 transition-transform" aria-hidden="true"></i>
        </button>
      )}
    </nav>
  );
};
